// EUR is the primary display currency everywhere. These helpers convert a
// native amount (CZK, PLN, …) to EUR using the rates from /api/rates and format
// either currency. Rates are "units per 1 EUR", so EUR = native / rate.

export type ExchangeRates = {
  base: string
  rates: Record<string, number>
  fetchedAt: string
}

/** Convert a native amount to EUR. Returns null when no rate is known (the
 *  caller should then fall back to showing the native amount, never blank). */
export function convertToEur(
  amount: number,
  currency: string,
  rates: ExchangeRates | undefined
): number | null {
  if (currency === "EUR") {
    return amount
  }
  const rate = rates?.rates[currency]
  if (!rate) {
    return null
  }
  return amount / rate
}

/** An amount to display. Exact figures (payout cycles, settlement totals) travel
 *  as decimal strings end-to-end and must be formatted digit-for-digit; computed
 *  figures (GMV, margins) are already numbers. */
export type MoneyAmount = number | string

export function formatCurrency(
  amount: MoneyAmount,
  currency: string,
  maximumFractionDigits = 2
): string {
  try {
    // Intl formats a decimal string exactly — no float round-trip — so a string
    // amount keeps every digit the marketplace sent. The cast is only needed
    // because the lib types narrow strings to a literal template type.
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      maximumFractionDigits,
    }).format(amount as unknown as number)
  } catch {
    const text = typeof amount === "number" ? amount.toFixed(maximumFractionDigits) : amount
    return `${text} ${currency}`
  }
}

/** A ROAS for a human: `1.73×`. Lives beside the currency formatter because the
 *  campaign card, its chart and the drawer all print it, and a second local copy
 *  is how an axis and a column start disagreeing about the same number. */
export function formatRoas(value: number): string {
  return `${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}\u00d7`
}
